import {
  ClickAwayListener,
  FormControl,
  Input,
  InputLabel,
  OutlinedInput,
  Paper,
  Popper,
  Stack,
  useTheme,
} from '@mui/material'
import { RichTreeView } from '@mui/x-tree-view'
import { useTreeViewApiRef } from '@mui/x-tree-view/hooks'
import type { TreeViewBaseItem } from '@mui/x-tree-view/models'
import type { FC } from 'react'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { ThemeMode } from 'types/config'
import type { Period } from 'types/periods'
import { parsePeriodDate } from 'types/periods'
import { TextField } from '../@extended/TextField'
import './SinglePeriodSelector.css'

interface SinglePeriodSelectorProps {
  periods: Period[]
  onChange: (periodId: string) => void
  value?: string
  rootPeriodOnly?: boolean
  depthOnly?: number
  excludePeriodId?: string
  sortOrder?: 'asc' | 'desc'
  timeFilter?: 'pastOnly' | 'futureOnly' | 'standard'
  className?: string
  style?: React.CSSProperties
  width?: string | number
  label?: string
  disabled?: boolean
  space?: number
  variant?: 'standard' | 'outlined'
  shrink?: boolean
  pill?: boolean
  placeholder?: string
  labelColor?: string
  labelBackgroundColor?: string
  disableClosed?: boolean
  noTruncate?: boolean
}

const SinglePeriodSelector: FC<SinglePeriodSelectorProps> = ({
  periods,
  onChange,
  value,
  rootPeriodOnly = false,
  depthOnly,
  excludePeriodId,
  sortOrder = 'asc',
  timeFilter = 'standard',
  className,
  style,
  width = '100%',
  label = 'Select Period',
  disabled = false,
  space = 0,
  variant = 'outlined',
  shrink = false,
  pill = false,
  placeholder = 'Select Period',
  labelColor = undefined,
  labelBackgroundColor = undefined,
  disableClosed = false,
  noTruncate = false,
}) => {
  const theme = useTheme()
  const apiRef = useTreeViewApiRef()
  const anchorRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  // Helper function to extract periods at a specific depth level
  const getPeriodsAtDepth = useCallback(
    (periods: Period[], targetDepth: number): Period[] => {
      const result: Period[] = []

      const traverse = (periodList: Period[], currentDepth: number) => {
        for (const period of periodList) {
          if (currentDepth === targetDepth) {
            // Exclude the period if it matches excludePeriodId
            if (!excludePeriodId || period.id !== excludePeriodId) {
              result.push({ ...period, children: [] }) // Remove children to show as flat list
            }
          }
          if (period.children && period.children.length > 0 && currentDepth < targetDepth) {
            traverse(period.children, currentDepth + 1)
          }
        }
      }

      traverse(periods, 0)
      return result
    },
    [excludePeriodId]
  )

  const filteredPeriods = useMemo(() => {
    const now = new Date()

    // First, filter by depth if depthOnly is specified
    let workingPeriods = periods
    if (depthOnly !== undefined) {
      workingPeriods = getPeriodsAtDepth(periods, depthOnly)
    }

    const filterPeriod = (period: Period): Period | null => {
      // Exclude the period if it matches excludePeriodId
      if (excludePeriodId && period.id === excludePeriodId) {
        return null
      }

      if (disableClosed && period.isClosed && period.rootPeriodId !== null && period.rootPeriodId === period.id) {
        return null
      }

      const startDate = parsePeriodDate(period.startDate)
      const endDate = parsePeriodDate(period.endDate)

      if (!startDate || !endDate) {
        return period // Include periods with invalid dates
      }

      let result: boolean
      switch (timeFilter) {
        case 'pastOnly':
          result = startDate <= now
          break
        case 'futureOnly':
          result = startDate > now
          break
        default:
          result = true
      }

      if (!result) return null

      // If depthOnly is set, we already have flat periods, so don't process children
      if (depthOnly !== undefined) {
        return period
      }

      if (period.children) {
        const filteredChildren = period.children.map(filterPeriod).filter((child): child is Period => child !== null)
        return { ...period, children: filteredChildren }
      }

      return period
    }

    const filtered = workingPeriods.map(filterPeriod).filter((period): period is Period => period !== null)
    return filtered
  }, [periods, timeFilter, disableClosed, depthOnly, excludePeriodId, getPeriodsAtDepth])

  const sortedPeriods = useMemo(() => {
    return [...filteredPeriods].sort((a, b) => {
      const dateA = new Date(a.endDate)
      const dateB = new Date(b.endDate)
      return sortOrder === 'asc' ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime()
    })
  }, [filteredPeriods, sortOrder])

  const renderTree = (nodes: Period[]): TreeViewBaseItem[] =>
    nodes.map((node) => ({
      id: node.id ?? '',
      label: node.label || '',
      children: !rootPeriodOnly && depthOnly === undefined && node.children ? renderTree(node.children) : [],
    }))

  const treeItems = useMemo(() => renderTree(sortedPeriods), [sortedPeriods, rootPeriodOnly, depthOnly])

  const firstRootItemId = useMemo(() => {
    return treeItems[0]?.id
  }, [treeItems])

  const handleItemSelectionToggle = useCallback(
    (event: React.SyntheticEvent, itemId: string, isSelected: boolean) => {
      if (isSelected) {
        onChange(itemId)
        setOpen(false)
      }
    },
    [onChange]
  )

  const handleInputClick = useCallback(() => {
    if (!disabled) {
      setOpen((prev) => !prev)
    }
  }, [disabled])

  const handleClickAway = useCallback(() => {
    setOpen(false)
  }, [])

  const getSelectedPeriodLabel = useMemo(() => {
    const findPeriod = (
      periods: Period[],
      rootLabel?: string,
      depth = 0
    ): { period: Period; rootLabel: string; depth: number } | undefined => {
      for (const period of periods) {
        if (period.id === value) {
          return { period, rootLabel: rootLabel || period.label, depth }
        }
        if (period.children) {
          const found = findPeriod(period.children, rootLabel || period.label, depth + 1)
          if (found) return found
        }
      }
      return undefined
    }

    const result = findPeriod(sortedPeriods)
    if (result) {
      const { period, rootLabel, depth } = result
      return depth === 0 ? period.label : `${period.label} (${rootLabel})`
    }
    return ''
  }, [value, sortedPeriods])

  const hoverColor = theme.palette.mode === ThemeMode.DARK ? theme.palette.action.hover : theme.palette.action.hover

  if (disabled) {
    return (
      <Stack spacing={space} sx={{ flexGrow: 1 }}>
        <TextField
          key="period-select"
          fullWidth
          variant="outlined"
          label={label}
          pill={pill}
          shrink={shrink}
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
          value={getSelectedPeriodLabel}
          sx={{
            '& .MuiInputBase-input': {
              color: 'text.secondary',
            },
          }}
        />
      </Stack>
    )
  }

  return (
    <div className={className} style={{ ...style, width }}>
      <FormControl variant={variant} sx={{ width }}>
        <InputLabel
          shrink
          variant={variant}
          sx={{
            '&.MuiInputLabel-shrink': {
              ...(labelBackgroundColor && { background: labelBackgroundColor }),
              color: labelColor || undefined,
              paddingLeft: '12px',
              paddingRight: '12px',
            },
          }}
        >
          {label}
        </InputLabel>

        <ClickAwayListener onClickAway={handleClickAway}>
          <div>
            {variant === 'standard' ? (
              <Input
                ref={anchorRef}
                readOnly
                value={getSelectedPeriodLabel || placeholder}
                onClick={handleInputClick}
                disabled={disabled}
                sx={{
                  cursor: disabled ? 'default' : 'pointer',
                  borderRadius: pill ? '21px' : '4px',
                  width,
                  '& .MuiInput-input': {
                    padding: noTruncate ? '10.5px 16px' : '10.5px 20px',
                    cursor: disabled ? 'default' : 'pointer',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: getSelectedPeriodLabel ? theme.palette.text.primary : theme.palette.text.secondary,
                  },
                }}
                inputProps={{
                  'aria-expanded': open,
                  'aria-haspopup': 'listbox',
                  'aria-label': label,
                  title: getSelectedPeriodLabel,
                }}
              />
            ) : (
              <OutlinedInput
                ref={anchorRef}
                readOnly
                value={getSelectedPeriodLabel || placeholder}
                onClick={handleInputClick}
                disabled={disabled}
                label={label}
                sx={{
                  cursor: disabled ? 'default' : 'pointer',
                  borderRadius: pill ? '21px' : '4px',
                  width,
                  '& .MuiOutlinedInput-input': {
                    padding: noTruncate ? '10.5px 16px' : '10.5px 20px',
                    cursor: disabled ? 'default' : 'pointer',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: getSelectedPeriodLabel ? theme.palette.text.primary : theme.palette.text.secondary,
                  },
                  '& .MuiOutlinedInput-root': {
                    width,
                  },
                }}
                inputProps={{
                  'aria-expanded': open,
                  'aria-haspopup': 'listbox',
                  'aria-label': label,
                  title: getSelectedPeriodLabel,
                }}
              />
            )}

            <Popper
              open={open}
              anchorEl={anchorRef.current}
              placement="bottom-start"
              sx={{ zIndex: theme.zIndex.modal }}
              modifiers={[
                {
                  name: 'flip',
                  enabled: true,
                },
                {
                  name: 'preventOverflow',
                  enabled: true,
                },
              ]}
            >
              <Paper
                sx={{
                  width: anchorRef.current?.offsetWidth || 'auto',
                  maxHeight: 300,
                  overflow: 'auto',
                  mt: 1,
                  boxShadow: theme.shadows[8],
                }}
              >
                <RichTreeView
                  multiSelect={false}
                  checkboxSelection
                  apiRef={apiRef}
                  items={treeItems}
                  selectedItems={value}
                  onItemSelectionToggle={handleItemSelectionToggle}
                  defaultExpandedItems={firstRootItemId ? [firstRootItemId] : []}
                  sx={{
                    '& .MuiTreeItem-content:hover': {
                      backgroundColor: hoverColor,
                    },
                    '& .MuiTreeItem-content.Mui-selected:hover': {
                      backgroundColor: theme.palette.action.selected,
                    },
                  }}
                />
              </Paper>
            </Popper>
          </div>
        </ClickAwayListener>
      </FormControl>
    </div>
  )
}

export default SinglePeriodSelector
